import React, { useState, useRef, useImperativeHandle, forwardRef, useEffect, useCallback, useMemo } from 'react';
import ReactDOM from 'react-dom';
import { FileItem } from '../../types/files';
import { getFileIcon } from '../../utils/fileIcons';
import { fileSystemService } from '../../services/fileSystem';
import { useTranslation } from 'react-i18next';
import './FileExplorer.css';
import { FaRegFile, FaRegFolder, FaSyncAlt, FaFolderMinus } from 'react-icons/fa';
import { useGlobalContext } from '../../contexts/GlobalContext';
import { useFileTree } from '../../contexts/FileTreeContext';
import { useCollab } from '../../contexts/CollabContext';
import { Mode } from 'types/modes';
import { readFileTree } from '../../utils/fileTree';
import { getParentPath } from 'utils/getParentPath';
import { FileNode } from './FileNode';
import { FolderNode } from './FolderNode';

interface FileExplorerProps {
  onFileClick: (file: FileItem, options?: { preview?: boolean }) => void;
  isSidebarVisible: boolean;
  onToggleSidebar: () => void;
  onFileDeleted: (file: FileItem) => void;
  onFileRenamed: (oldFile: FileItem, newFile: FileItem) => void;
  previewTab: FileItem | null;
}

interface ContextMenuPosition {
  x: number;
  y: number;
}

interface ClipboardState {
  type: 'copy' | 'cut' | null;
  item: FileItem | null;
}

type NewItemType = 'file' | 'folder' | null;

const FileExplorer = forwardRef<any, FileExplorerProps>(({
  onFileClick,
  isSidebarVisible,
  onToggleSidebar,
  onFileDeleted,
  onFileRenamed,
  previewTab,
}, ref) => {

  const {
    mode,
    projectRootPath,
    activeTab,
    getCurrentFileTree
  } = useGlobalContext();
  const {
    refreshFileTree
  } = useFileTree();
  const collab = useCollab();

  // Get the appropriate file tree based on current mode
  const currentFileTree = getCurrentFileTree();

  const [localActiveFile, setLocalActiveFile] = useState<FileItem | null>(null);
  const { t } = useTranslation('FileExplorer');

  const [draggedPath, setDraggedPath] = useState<string | null>(null);
  const [dropTargetPath, setDropTargetPath] = useState<string | null>(null);
  const [newItemType, setNewItemType] = useState<NewItemType>(null);
  const [newItemName, setNewItemName] = useState('');
  const [renameItem, setRenameItem] = useState<FileItem | null>(null);
  const [renameValue, setRenameValue] = useState('');
  const [clipboard, setClipboard] = useState<ClipboardState>({ type: null, item: null });
  const [contextMenu, setContextMenu] = useState<{
    show: boolean;
    position: ContextMenuPosition;
    targetItem: FileItem | null;
  }>({
    show: false,
    position: { x: 0, y: 0 },
    targetItem: null,
  });
  const [selectedItemPath, setSelectedItemPath] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);
  const [newItemParentPath, setNewItemParentPath] = useState<string | null>(null);
  const [collapseAllTrigger, setCollapseAllTrigger] = useState(0);

  // Memoized shouldShowItem function
  const shouldShowItem = useCallback((item: FileItem): boolean => {
    const hiddenFolders = ['.git', '.vs', '.vscode', '.github', 'bin', 'obj', '.config'];
    return !item.isDirectory || !hiddenFolders.includes(item.name);
  }, []);

  // Memoized sortFileTree function
  const sortFileTree = useCallback((items: FileItem[]): FileItem[] => {
    const filtered = items.filter(shouldShowItem);
    const sorted = [...filtered].sort((a, b) => {
      if (a.isDirectory && !b.isDirectory) return -1;
      if (!a.isDirectory && b.isDirectory) return 1;
      return a.name.localeCompare(b.name);
    });
    return sorted.map(item => {
      const children = (item as any).children;
      if (item.isDirectory && Array.isArray(children)) {
        return { ...item, children: sortFileTree(children) };
      }
      return item;
    });
  }, [shouldShowItem]);

  // Memoized sortedFileTree
  const sortedFileTree = useMemo(() => {
    return sortFileTree(currentFileTree);
  }, [currentFileTree, sortFileTree]);

  useEffect(() => {
    if (activeTab?.file) {
      setLocalActiveFile(activeTab.file);
    } else {
      setLocalActiveFile(null);
    }
  }, [activeTab]);

  // Memoized handlers
  const handleDragStart = useCallback((path: string) => {
    setDraggedPath(path);
  }, []);

  const handleDragOver = useCallback((e: React.DragEvent, path: string) => {
    e.preventDefault();
    setDropTargetPath(path);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent, path: string) => {
    setDropTargetPath(prev => (prev === path ? null : prev));
  }, []);

  const handleDrop = useCallback(async (e: React.DragEvent, targetFolder: FileItem) => {
    e.preventDefault();
    setDropTargetPath(null);
    if (!draggedPath || draggedPath === targetFolder.path) return;
    try {
      await fileSystemService.moveFileOrFolder(draggedPath, targetFolder.path);
      if (currentFileTree.some(f => f.path === draggedPath)) {
        if (!projectRootPath) {
          return;
        }
        await refreshFileTree();
      }
    } catch (err) {
      alert(t('errors.moveFailed', { error: (err as Error).message }));
    }
    setDraggedPath(null);
  }, [draggedPath, currentFileTree, projectRootPath, refreshFileTree, t]);

  const handleSelect = useCallback((path: string) => {
    setSelectedItemPath(path);
  }, []);

  const handleRenameChange = useCallback((value: string) => {
    setRenameValue(value);
  }, []);

  const handleRenameBlur = useCallback(() => {
    setRenameItem(null);
    setRenameValue('');
  }, []);

  const handleNewItemNameChange = useCallback((value: string) => {
    setNewItemName(value);
  }, []);

  const handleNewItemBlur = useCallback(() => {
    setNewItemType(null);
    setNewItemName('');
    setNewItemParentPath(null);
  }, []);

  // Memoized determineNewItemParentPath
  const determineNewItemParentPath = useCallback((targetItem?: FileItem | null): string | null => {
    if (targetItem) {
      return targetItem.isDirectory ? targetItem.path : targetItem.path.substring(0, targetItem.path.lastIndexOf('\\') > targetItem.path.lastIndexOf('/') ? targetItem.path.lastIndexOf('\\') : targetItem.path.lastIndexOf('/'));
    } else if (selectedItemPath) {
      const selectedItem = findItemByPath(currentFileTree, selectedItemPath);
      if (selectedItem) {
        return selectedItem.isDirectory ? selectedItem.path : selectedItem.path.substring(0, selectedItem.path.lastIndexOf('\\') > selectedItem.path.lastIndexOf('/') ? selectedItem.path.lastIndexOf('\\') : selectedItem.path.lastIndexOf('/'));
      }
    }
    return projectRootPath;
  }, [selectedItemPath, currentFileTree, projectRootPath]);

  // Memoized findItemByPath
  const findItemByPath = useCallback((items: FileItem[], itemPath: string): FileItem | undefined => {
    for (const item of items) {
      if (item.path === itemPath) {
        return item;
      }
      if (item.isDirectory) {
        const children = (item as any).children;
        if (children) {
          const found = findItemByPath(children, itemPath);
          if (found) {
            return found;
          }
        }
      }
    }
    return undefined;
  }, []);

  // Memoized handleRefresh
  const handleRefresh = useCallback(async () => {
    if (mode === Mode.Join && collab.sessionId && collab.connection) {
      collab.requestFileTreeFromHost();
    } else {
      await refreshFileTree();
    }

    if (mode === Mode.Host) {
      await collab.sendUpdatedFileTreeToBackend();
    }
  }, [mode, collab, refreshFileTree]);

  // Memoized handleInputKeyDown
  const handleInputKeyDown = useCallback(async (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && (newItemName.trim() || renameValue.trim())) {
      if (renameItem) {
        // Yeniden adlandırma
        const pathSeparator = renameItem.path.includes('\\') ? '\\' : '/';
        const parentPath = renameItem.path.substring(0, renameItem.path.lastIndexOf(pathSeparator));
        const newPath = `${parentPath}${pathSeparator}${renameValue.trim()}`;
        try {
          await fileSystemService.renameFileOrFolder(renameItem.path, newPath);
          const newFile = {
            ...renameItem,
            name: renameValue.trim(),
            path: newPath
          };
          onFileRenamed(renameItem, newFile);
          await handleRefresh();
        } catch (err) {
          console.error('Rename error:', err);
          alert(t('errors.renameFailed', { error: (err as Error).message }));
        }
        setRenameItem(null);
        setRenameValue('');
      } else if (newItemType && newItemParentPath) {
        // Yeni dosya/klasör
        const pathSeparator = newItemParentPath.includes('\\') ? '\\' : '/';
        const fullPath = `${newItemParentPath}${pathSeparator}${newItemName.trim()}`;
        try {
          if (newItemType === 'file') {
            await fileSystemService.writeFile(fullPath, '');
          } else if (newItemType === 'folder') {
            await fileSystemService.createDirectory(fullPath);
          }
          await handleRefresh();
        } catch (err) {
          alert(t('errors.createFailed', { error: (err as Error).message }));
        }
        setNewItemType(null);
        setNewItemName('');
        setNewItemParentPath(null);
      }
    } else if (e.key === 'Escape') {
      setNewItemType(null);
      setNewItemName('');
      setRenameItem(null);
      setRenameValue('');
      setNewItemParentPath(null);
    }
  }, [newItemName, renameValue, renameItem, newItemType, newItemParentPath, onFileRenamed, handleRefresh, t]);

  useImperativeHandle(ref, () => ({
    openNewFileInput: () => {
      setNewItemType('file');
      setNewItemName('');
      setNewItemParentPath(determineNewItemParentPath());
      setTimeout(() => inputRef.current?.focus(), 0);
    },
    openNewFolderInput: () => {
      setNewItemType('folder');
      setNewItemName('');
      setNewItemParentPath(determineNewItemParentPath());
      setTimeout(() => inputRef.current?.focus(), 0);
    },
    refreshFiles: handleRefresh,
    collapseAll: () => setCollapseAllTrigger(prev => prev + 1)
  }), [determineNewItemParentPath, handleRefresh]);

  // Memoized context menu handler
  const handleContextMenu = useCallback((e: React.MouseEvent, item: FileItem) => {
    e.preventDefault();
    setContextMenu({
      show: true,
      position: { x: e.clientX, y: e.clientY },
      targetItem: item,
    });
    setSelectedItemPath(item.path);
  }, []);

  const handleContextMenuClose = useCallback(() => {
    setContextMenu({ show: false, position: { x: 0, y: 0 }, targetItem: null });
  }, []);

  const handleContextMenuAction = useCallback(async (action: string) => {
    const item = contextMenu.targetItem;
    if (!item) return;

    try {
      switch (action) {
        case 'newFile':
          setNewItemType('file');
          setNewItemName('');
          setNewItemParentPath(determineNewItemParentPath(item));
          setTimeout(() => inputRef.current?.focus(), 0);
          break;
        case 'newFolder':
          setNewItemType('folder');
          setNewItemName('');
          setNewItemParentPath(determineNewItemParentPath(item));
          setTimeout(() => inputRef.current?.focus(), 0);
          break;
        case 'copy':
          setClipboard({ type: 'copy', item: item });
          break;
        case 'cut':
          setClipboard({ type: 'cut', item: item });
          break;
        case 'paste':
          if (clipboard.item && clipboard.type) {
            const targetPath = item.isDirectory
              ? item.path
              : item.path.substring(0, item.path.lastIndexOf('/'));

            const newPath = `${targetPath}/${clipboard.item.name}`;

            try {
              if (clipboard.type === 'copy') {
                await fileSystemService.copyFileOrFolder(clipboard.item.path, newPath);
              } else {
                await fileSystemService.moveFileOrFolder(clipboard.item.path, newPath);
              }
              await handleRefresh();
              if (clipboard.type === 'cut') {
                setClipboard({ type: null, item: null });
              }
            } catch (err) {
              alert(t('errors.copyFailed', { error: (err as Error).message }));
            }
          }
          break;
        case 'rename':
          setRenameItem(item);
          setRenameValue(item.name);
          setTimeout(() => inputRef.current?.focus(), 0);
          break;
        case 'delete':
          if (window.confirm(t('confirmations.delete', { name: item.name }))) {
            try {
              const result = await fileSystemService.deleteFileOrFolder(item.path);
              if (!result.success) {
                throw new Error(result.error || t('errors.deleteFailed'));
              }
              onFileDeleted(item);
              await handleRefresh();
            } catch (err) {
              console.error('Delete error:', err);
              alert(t('errors.deleteFailed', { error: (err as Error).message }));
            }
          }
          break;
        case 'copyPath':
          navigator.clipboard.writeText(item.path);
          break;
        case 'revealInExplorer':
          try {
            await fileSystemService.revealInExplorer(item.path);
          } catch (err) {
            alert(t('errors.revealFailed', { error: (err as Error).message }));
          }
          break;
      }
    } catch (err) {
      alert(t('errors.createFailed', { error: (err as Error).message }));
    }
    handleContextMenuClose();
  }, [contextMenu.targetItem, clipboard, determineNewItemParentPath, handleRefresh, onFileDeleted, handleContextMenuClose, t]);

  // Sayfa tıklamasında menüyü kapat
  useEffect(() => {
    const handleClick = () => setContextMenu(prev => ({ ...prev, show: false }));
    document.addEventListener('click', handleClick);
    return () => document.removeEventListener('click', handleClick);
  }, []);

  useEffect(() => {
    if (renameItem && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    } else if ((newItemType === 'file' || newItemType === 'folder') && inputRef.current) {
      inputRef.current.focus();
    }
  }, [renameItem, newItemType]);

  // Memoized filterFiles function
  const filterFiles = useCallback((items: FileItem[]): FileItem[] => {
    if (!searchQuery) return items;

    return items.filter(item => {
      const matchesSearch = item.name.toLowerCase().includes(searchQuery.toLowerCase());
      if (item.isDirectory) {
        const children = (item as any).children;
        if (children) {
          const filteredChildren = filterFiles(children);
          return matchesSearch || filteredChildren.length > 0;
        }
      }
      return matchesSearch;
    });
  }, [searchQuery]);

  // Memoized getNodes function - EN ÖNEMLİ OPTİMİZASYON
  const getNodes = useMemo(() => {
    const filteredItems = filterFiles(sortedFileTree);
    const nodes: React.ReactNode[] = [];

    // Root level new item input
    if (newItemType && newItemParentPath === projectRootPath && !selectedItemPath) {
      nodes.push(
        <div className="file-tree-item new-item" key="new-item-root" style={{ paddingLeft: '0px' }}>
          <span className="file-icon">
            {newItemType === 'file' ?
              <FaRegFile className="icon" /> :
              <FaRegFolder className="icon" />
            }
          </span>
          <input
            ref={inputRef}
            value={newItemName}
            onChange={e => setNewItemName(e.target.value)}
            onKeyDown={handleInputKeyDown}
            placeholder={t(`placeholders.${newItemType === 'file' ? 'newFile' : 'newFolder'}`)}
            className="new-item-input"
            autoFocus
            onBlur={handleNewItemBlur}
            style={{ width: 140 }}
          />
        </div>
      );
    }

    for (const item of filteredItems) {
      if (item.isDirectory) {
        nodes.push(
          <FolderNode
            key={item.path}
            item={item}
            level={0}
            isSelected={selectedItemPath === item.path}
            onFileClick={onFileClick}
            onContextMenu={handleContextMenu}
            onDragStart={handleDragStart}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            onSelect={handleSelect}
            renameItem={renameItem}
            renameValue={renameValue}
            onRenameChange={handleRenameChange}
            onRenameKeyDown={handleInputKeyDown}
            onRenameBlur={handleRenameBlur}
            dropTargetPath={dropTargetPath}
            localActiveFile={localActiveFile}
            previewTab={previewTab}
            newItemType={newItemType}
            newItemParentPath={newItemParentPath}
            newItemName={newItemName}
            onNewItemNameChange={handleNewItemNameChange}
            onNewItemKeyDown={handleInputKeyDown}
            onNewItemBlur={handleNewItemBlur}
            newItemInputRef={inputRef}
            searchQuery={searchQuery}
            shouldShowItem={shouldShowItem}
            sortFileTree={sortFileTree}
            selectedItemPath={selectedItemPath}
            onCollapseAll={collapseAllTrigger > 0 ? () => { } : undefined}
          />
        );
      } else {
        nodes.push(
          <FileNode
            key={item.path}
            item={item}
            level={0}
            isActive={localActiveFile?.path === item.path}
            isSelected={selectedItemPath === item.path}
            isPreview={previewTab?.path === item.path}
            onFileClick={onFileClick}
            onContextMenu={handleContextMenu}
            onDragStart={handleDragStart}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            onSelect={handleSelect}
            renameItem={renameItem}
            renameValue={renameValue}
            onRenameChange={handleRenameChange}
            onRenameKeyDown={handleInputKeyDown}
            onRenameBlur={handleRenameBlur}
            dropTargetPath={dropTargetPath}
          />
        );
      }
    }

    return nodes;
  }, [
    // Kritik dependencies - sadece gerçekten değişmesi gereken şeyler
    sortedFileTree,
    searchQuery,
    newItemType,
    newItemParentPath,
    projectRootPath,
    selectedItemPath,
    newItemName,
    renameItem,
    renameValue,
    dropTargetPath,
    localActiveFile,
    previewTab,
    collapseAllTrigger,
    // Memoized handlers
    onFileClick,
    handleContextMenu,
    handleDragStart,
    handleDragOver,
    handleDragLeave,
    handleDrop,
    handleSelect,
    handleRenameChange,
    handleInputKeyDown,
    handleRenameBlur,
    handleNewItemNameChange,
    handleNewItemBlur,
    shouldShowItem,
    sortFileTree,
    filterFiles,
    t
  ]);

  // Memoized ContextMenu component
  const ContextMenuComponent = useMemo(() => {
    const ContextMenu = ({ show, position, onAction }: any) => {
      if (!show) return null;
      return ReactDOM.createPortal(
        <div
          className="context-menu"
          style={{
            position: 'fixed',
            top: position.y,
            left: position.x,
            zIndex: 99999,
          }}
        >
          <div className="context-menu-item" onClick={() => onAction('newFile')}>
            {t('contextMenu.newFile')}
          </div>
          <div className="context-menu-item" onClick={() => onAction('newFolder')}>
            {t('contextMenu.newFolder')}
          </div>
          <div className="context-menu-separator" />
          <div className="context-menu-item" onClick={() => onAction('copy')}>
            {t('contextMenu.copy')}
          </div>
          <div className="context-menu-item" onClick={() => onAction('cut')}>
            {t('contextMenu.cut')}
          </div>
          <div className="context-menu-item"
            onClick={() => onAction('paste')}
            style={{ opacity: clipboard.item ? 1 : 0.5, cursor: clipboard.item ? 'pointer' : 'not-allowed' }}>
            {t('contextMenu.paste')}
          </div>
          <div className="context-menu-separator" />
          <div className="context-menu-item" onClick={() => onAction('rename')}>
            {t('contextMenu.rename')}
          </div>
          <div className="context-menu-item" onClick={() => onAction('delete')}>
            {t('contextMenu.delete')}
          </div>
          <div className="context-menu-separator" />
          <div className="context-menu-item" onClick={() => onAction('copyPath')}>
            {t('contextMenu.copyPath')}
          </div>
          <div className="context-menu-item" onClick={() => onAction('revealInExplorer')}>
            {t('contextMenu.revealInExplorer')}
          </div>
        </div>,
        document.body
      );
    };
    return ContextMenu;
  }, [t, clipboard.item]);

  // Header buttons handlers
  const handleNewFileClick = useCallback(() => {
    setNewItemType('file');
    setNewItemName('');
    setNewItemParentPath(determineNewItemParentPath());
    setTimeout(() => inputRef.current?.focus(), 0);
  }, [determineNewItemParentPath]);

  const handleNewFolderClick = useCallback(() => {
    setNewItemType('folder');
    setNewItemName('');
    setNewItemParentPath(determineNewItemParentPath());
    setTimeout(() => inputRef.current?.focus(), 0);
  }, [determineNewItemParentPath]);

  const handleCollapseAllClick = useCallback(() => {
    setCollapseAllTrigger(prev => prev + 1);
  }, []);

  const handleSearchChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value);
  }, []);

  return (
    <div className={`sidebar ${isSidebarVisible ? 'visible' : 'hidden'}`}>
      <div className="sidebar-header">
        <button className="sidebar-toggle" onClick={onToggleSidebar}>
          {isSidebarVisible ? '◀' : '▶'}
        </button>
        <span>EXPLORER</span>
        <div className="explorer-actions">
          <button title={t('buttons.newFile')} onClick={handleNewFileClick}>
            <FaRegFile />
          </button>
          <button title={t('buttons.newFolder')} onClick={handleNewFolderClick}>
            <FaRegFolder />
          </button>
          <button title={t('buttons.refresh')} onClick={handleRefresh}>
            <FaSyncAlt />
          </button>
          <button title={t('buttons.collapseAll')} onClick={handleCollapseAllClick}>
            <FaFolderMinus />
          </button>
        </div>
      </div>
      <div className="file-explorer-search">
        <input
          type="text"
          placeholder={t('search.placeholder', 'Search files...')}
          value={searchQuery}
          onChange={handleSearchChange}
        />
      </div>
      <div className="file-tree">
        {getNodes}
      </div>
      <ContextMenuComponent
        show={contextMenu.show}
        position={contextMenu.position}
        onAction={handleContextMenuAction}
      />
    </div>
  );
});

FileExplorer.displayName = 'FileExplorer';

// Component'i memoize et - ÇOK ÖNEMLİ!
export default React.memo(FileExplorer);